<template>
  <div v-if="order.meta && order.meta.shippings">
    <b-card>
      <b-form-row>
        <b-col cols="6">
          <b-form inline>
            <label class="mr-sm-2" for="pickupDelay" v-translate>Interval Between Pickups</label>
            <b-input type="number" id="pickupDelay" v-model="pickupDelay"></b-input>
          </b-form>
        </b-col>
        <b-col cols="6">
          <b-form inline>
            <label class="mr-sm-2" for="estimatedDriveTime" v-translate>Estimated Drive Time</label>
            <b-input type="number" id="estimatedDriveTime" v-model="estimatedDriveTime"></b-input>
          </b-form>
        </b-col>
      </b-form-row>
    </b-card>

    <b-card>
      <b-form-row>
        <b-col cols="10">
          <b-form inline>
            <label class="mr-sm-2 mb-sm-0" for="bulkCarrier" v-translate>Bulk Set Carrier</label>
            <company-autocomplete ref="bulkCarrier" id="bulkCarrier" style="display: inline-block; width: 20em; vertical-align: middle;" class="mr-sm-2 mb-sm-0" @selected="setBulkCarrier" :restrict-to="[['relationship', 'eq', 'provider']]" />
            <span class="mr-sm-2 mb-sm-0">×</span>
            <b-input type="number" id="bulkCarrierQty" v-model="bulkCarrierQty" class="mr-sm-2 mb-sm-0" style="width: 3.5rem"></b-input>
            <label class="mr-sm-2 mb-sm-0" for="estimatedDriveTime" v-translate>Shippings</label>

            <b-button size="sm" variant="success" @click="applyBulkSetCarrier" :disabled="order.meta.shippings.map(x => x.courier_company_id || null).filter(Boolean).length === order.meta.shippings.length" v-translate>Set</b-button>

            <b-button size="sm" variant="danger" class="ml-1" @click="resetCarriers" :disabled="order.meta.shippings.map(x => x.courier_company_id || null).filter(Boolean).length === 0" v-translate>Reset Carriers</b-button>
          </b-form>
        </b-col>
        <b-col style="display: flex; align-items: center; justify-content: center; margin-bottom: 0;" :class="order.meta.shippings.map(x => x.courier_company_id || null).filter(Boolean).length === order.meta.shippings.length ? 'text-success' : order.meta.shippings.map(x => x.courier_company_id || null).filter(Boolean).length !== 0 ? 'text-warning' : 'text-danger'">
          <strong>{{ order.meta.shippings.map(x => x.courier_company_id || null).filter(Boolean).length }}/{{ order.meta.shippings.length }}</strong>
        </b-col>
      </b-form-row>
    </b-card>

    <b-card v-for="(shipping, index) in order.meta.shippings" :key="'shipping-' + index">
      <h5 v-translate="{number: index + 1}">Shipment %{number}</h5>
      <b-form-row class="mb-3">
        <b-col sm="4" lg="3">
          <label :for="'pickup-date' + index" class="col-form-label" v-translate>Estimated Pickup Date</label>
        </b-col>
        <b-col>
          <b-row>
            <b-col>
              <b-form-input :id="'pickup-date' + index" type="date" v-model="shipping.estimated_pickup_dateDateTimeParts[0]" />
            </b-col>
            <b-col>
              <b-form-input type="time" v-model="shipping.estimated_pickup_dateDateTimeParts[1]" />
            </b-col>
          </b-row>
        </b-col>
      </b-form-row>
      <b-form-row class="mb-3">
        <b-col sm="4" lg="3">
          <label :for="'pickup-note' + index" class="col-form-label" v-translate>Pickup Note</label>
        </b-col>
        <b-col>
          <b-form-textarea :id="'pickup-note' + index" v-model="shipping.pickup_note" />
        </b-col>
      </b-form-row>
      <b-form-row class="mb-3">
        <b-col sm="4" lg="3">
          <label :for="'delivery-date' + index" class="col-form-label" v-translate>Estimated Delivery Date</label>
        </b-col>
        <b-col>
          <b-row>
            <b-col>
              <b-form-input :id="'delivery-date' + index" type="date" v-model="shipping.estimated_delivery_dateDateTimeParts[0]" />
            </b-col>
            <b-col>
              <b-form-input type="time" v-model="shipping.estimated_delivery_dateDateTimeParts[1]" />
            </b-col>
          </b-row>
        </b-col>
      </b-form-row>
      <b-form-row class="mb-3">
        <b-col sm="4" lg="3">
          <label :for="'delivery-note' + index" class="col-form-label" v-translate>Delivery Note</label>
        </b-col>
        <b-col>
          <b-form-textarea :id="'delivery-note' + index" v-model="shipping.delivery_note" />
        </b-col>
      </b-form-row>
      <b-form-row class="mb-3">
        <b-col sm="4" lg="3">
          <label :for="'courier-company-id' + index" class="col-form-label" v-translate>Carrier</label>
        </b-col>
        <b-col>
          <company-autocomplete :id="'courier-company-id' + index" v-model="shipping.courier_company_id" @selected="shipping.courier_company_id = $event.id; shipping.courier_company = {id: $event.id, external_id: $event.external_id, name: $event.name}" :required="order.status !== 'draft'" :restrict-to="[['relationship', 'eq', 'provider']]" :pre-query="shipping.courier_company && shipping.courier_company.name ? `${shipping.courier_company.name}${shipping.courier_company.external_id ? ` (${$options.filters.cleanentityprefix(shipping.courier_company.external_id)})` : ''}` : null" />
        </b-col>
      </b-form-row>
    </b-card>

    <b-card class="mb-0">
      <h5 v-translate>Purchase Orders</h5>
      <div v-for="(purchaseOrder, index) in order.meta.purchase_orders" :key="'order-' + index">
        <b-input-group class="mb-3">
          <b-input type="text" v-model="order.meta.purchase_orders[index]" />
          <b-input-group-append>
            <b-button size="sm" variant="success" :disabled="!order.meta.purchase_orders[index]" v-if="index === order.meta.purchase_orders.length - 1" @click="order.meta.purchase_orders.push('')"><strong>＋</strong></b-button>
            <b-button size="sm" variant="danger" :disabled="!order.meta.purchase_orders[index]" v-if="order.meta.purchase_orders.length > 1" @click="order.meta.purchase_orders.splice(index, 1)">✕</b-button>
          </b-input-group-append>
        </b-input-group>
      </div>
    </b-card>
  </div>
</template>

<script>
import CompanyAutocomplete from '@/components/Autocompletes/CompanyAutocomplete'

const metaShippingTemplate = {
  estimated_pickup_date: null,
  estimated_delivery_date: null,
  pickup_note: null,
  delivery_note: null,
  courier_company_id: null,
  courier_company: {}
}

export default {
  name: 'EditFieldMeta',
  components: {
    CompanyAutocomplete
  },
  props: {
    order: {
      type: Object,
      default: () => {}
    }
  },
  data() {
    return {
      pickupDelay: null,
      estimatedDriveTime: null,
      settingDelay: false,
      bulkCarrier: null,
      bulkCarrierQty: 1
    }
  },
  computed: {
    firstPickupDate() {
      return this.order?.meta?.shippings?.[0]?.estimated_pickup_date
    }
  },
  watch: {
    order: {
      deep: true,
      handler(value) {
        if (Array.isArray(this.order.meta) && this.order.meta.length === 0) {
          this.order.meta = {}
        }
        if (!this.order.meta?.shippings) {
          this.$set(this.order.meta, 'shippings', [])
          for (let i = 0; i < this.order.shippings_amount; i++) {
            this.order.meta.shippings.push({ ...metaShippingTemplate })
          }
        }
        if (!this.order.meta?.purchase_orders) {
          this.$set(this.order.meta, 'purchase_orders', [''])
        }

        const carriers = this.order.meta.shippings.map(x => x.courier_company || null).filter(Boolean)

        if (this.order.courier_company_id && this.order.meta.shippings.length > 0 && carriers.length === 0) {
          this.order.meta.shippings = this.order.meta.shippings.map(x => {
            x.courier_company_id = this.order.courier_company_id
            x.courier_company = {
              id: this.order.courier_company.id,
              external_id: this.order.courier_company.external_id,
              name: this.order.courier_company.name
            }
            return x
          })
        }
      }
    },
    'order.meta.shippings': {
      immediate: true,
      deep: true,
      handler(newValue) {
        if (newValue) {
          this.bulkCarrierQty = newValue.length
          for (let index = 0; index < newValue.length; index++) {
            const shipping = newValue[index]
            if (index === 0) {
              this.order.courier_company_id = shipping.courier_company_id
            }
            if (!this.order.meta.shippings[index].estimated_pickup_dateDateTimeParts) {
              this.$set(this.order.meta.shippings[index], 'estimated_pickup_dateDateTimeParts', (shipping.estimated_pickup_date
                ? [
                    shipping.estimated_pickup_date.split(' ')[0],
                    shipping.estimated_pickup_date.split(' ')[1] || '00:00'
                  ]
                : ['', '00:00']))
            }
            if (!this.order.meta.shippings[index].estimated_delivery_dateDateTimeParts) {
              this.$set(this.order.meta.shippings[index], 'estimated_delivery_dateDateTimeParts', (shipping.estimated_delivery_date
                ? [
                    shipping.estimated_delivery_date.split(' ')[0],
                    shipping.estimated_delivery_date.split(' ')[1] || '00:00'
                  ]
                : ['', '00:00']))
            }
            this.order.meta.shippings[index].estimated_pickup_date = this.order.meta.shippings[index].estimated_pickup_dateDateTimeParts[0] ? this.moment.tz(this.order.meta.shippings[index].estimated_pickup_dateDateTimeParts.join(' '), this.$tz).utc().format('YYYY-MM-DD HH:mm:ss') : null
            this.order.meta.shippings[index].estimated_delivery_date = this.order.meta.shippings[index].estimated_delivery_dateDateTimeParts[0] ? this.moment.tz(this.order.meta.shippings[index].estimated_delivery_dateDateTimeParts.join(' '), this.$tz).utc().format('YYYY-MM-DD HH:mm:ss') : null
          }
        }
      }
    },
    firstPickupDate: {
      immediate: true,
      deep: true,
      handler(newValue) {
        if (newValue) {
          if (!this.settingDelay && this.pickupDelay) {
            this.setPickupDelay()
          }
          if (!this.settingDelay && this.estimatedDriveTime) {
            this.setDriveTime()
          }
        }
      }
    },
    pickupDelay(val) {
      this.setPickupDelay()
      this.setDriveTime()
    },
    estimatedDriveTime(val) {
      this.setDriveTime()
    }
  },
  methods: {
    setPickupDelay() {
      if (this.pickupDelay && !this.settingDelay) {
        for (let i = 1; i < this.order.meta.shippings.length; i++) {
          this.settingDelay = true
          const prevShipping = this.order.meta.shippings[i - 1]
          if (prevShipping.estimated_pickup_date) {
            this.order.meta.shippings[i].estimated_pickup_date = this.moment.utc(prevShipping.estimated_pickup_date).add(this.pickupDelay, 'minutes').utc().format('YYYY-MM-DD HH:mm:ss')
            this.order.meta.shippings[i].estimated_pickup_dateDateTimeParts = this.moment.utc(this.order.meta.shippings[i].estimated_pickup_date).tz(this.$tz).format('YYYY-MM-DD HH:mm:ss').split(' ')
          }
        }
        this.settingDelay = false
      }
    },
    setDriveTime() {
      if (this.estimatedDriveTime && !this.settingDelay) {
        let i = 0
        for (const shipping of this.order.meta.shippings) {
          this.settingDelay = true
          if (shipping.estimated_pickup_date) {
            this.order.meta.shippings[i].estimated_delivery_date = this.moment.utc(shipping.estimated_pickup_date).add(this.estimatedDriveTime, 'minutes').utc().format('YYYY-MM-DD HH:mm:ss')
            this.order.meta.shippings[i].estimated_delivery_dateDateTimeParts = this.moment.utc(this.order.meta.shippings[i].estimated_delivery_date).tz(this.$tz).format('YYYY-MM-DD HH:mm:ss').split(' ')
          }
          i++
        }
        this.settingDelay = false
      }
    },
    setBulkCarrier(item) {
      this.bulkCarrier = {
        courier_company_id: item.id,
        courier_company: {
          id: item.id,
          external_id: item.external_id,
          name: item.name
        }
      }
    },
    applyBulkSetCarrier() {
      let i = this.bulkCarrierQty
      this.order.meta.shippings = this.order.meta.shippings.map(x => {
        if (!x.courier_company_id && i > 0) {
          x = { ...x, ...this.bulkCarrier }
          i = i - 1
        }
        return x
      })
      this.bulkCarrier = null
      this.$refs.bulkCarrier.searchQuery = ''
    },
    resetCarriers() {
      this.order.meta.shippings = this.order.meta.shippings.map(x => {
        x.courier_company_id = null
        x.courier_company = {}
        return x
      })
    }
  }
}
</script>
